/**
 * Store Error Helper Utilities
 *
 * Provides type-safe helpers for checking and comparing store errors
 */

import { StoreError } from '../types/storeError.interface';

/**
 * Type guard to check if error matches a specific error code
 *
 * @param error - store error object (may be null/undefined)
 * @param code - Error code to check (use ErrorCodes constants)
 * @returns True if error matches the code
 *
 * @example
 * import { ErrorCodes } from '@/constants/errorCodes';
 *
 * if (isErrorCode(authState.error, ErrorCodes.BUS_NOT_FOUND)) {
 *   // Handle not found error
 * }
 */
export function isErrorCode(
  error: StoreError | null | undefined,
  code: string,
): boolean {
  return error !== null && error !== undefined && error.code === code;
}

/**
 * Check if error matches any of the provided error codes
 *
 * @param error - store error object (may be null/undefined)
 * @param codes - Array of error codes to check against
 * @returns True if error matches any of the codes
 *
 * @example
 * import { ErrorCodes } from '@/constants/errorCodes';
 *
 * if (isAnyErrorCode(error, [
 *   ErrorCodes.AUTH_UNAUTHENTICATED,
 *   ErrorCodes.AUTH_TOKEN_EXPIRED
 * ])) {
 *   // Handle auth expiration
 * }
 */
export function isAnyErrorCode(
  error: StoreError | null | undefined,
  codes: string[],
): boolean {
  return error !== null && error !== undefined && codes.includes(error.code);
}

/**
 * Check if error matches a specific category
 *
 * @param error - store error object (may be null/undefined)
 * @param category - Error category to check
 * @returns True if error matches the category
 *
 * @example
 * if (isErrorCategory(error, 'auth')) {
 *   // Handle all authentication errors
 *   authService.logout();
 * }
 */
export function isErrorCategory(
  error: StoreError | null | undefined,
  category: 'auth' | 'validation' | 'business' | 'network' | 'server',
): boolean {
  return error !== null && error !== undefined && error.category === category;
}

/**
 * Get error message from error object, with fallback
 *
 * @param error - store error object (may be null/undefined)
 * @param fallback - Fallback message if error is null/undefined
 * @returns Error message or fallback
 *
 * @example
 * const message = getErrorMessage(error, 'An unknown error occurred');
 * alert(message);
 */
export function getErrorMessageText(
  error: StoreError | null | undefined,
  fallback = 'An error occurred',
): string {
  return error?.message || fallback;
}

/**
 * Check if error exists (not null/undefined)
 *
 * @param error - store error object (may be null/undefined)
 * @returns True if error exists
 */
export function hasError(
  error: StoreError | null | undefined,
): error is StoreError {
  return error !== null && error !== undefined;
}
